import React, { useState, useEffect } from 'react';
import { 
  Home, 
  Code2, 
  TrendingUp, 
  Briefcase, 
  Menu, 
  X, 
  Sparkles, 
  Calculator, 
  Cpu, 
  HelpCircle, 
  Mail, 
  MessageCircle,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Zap,
  PhoneCall,
  Globe,
  BookOpen
} from 'lucide-react';
import { Language, AppPage } from '../types';
import { translations } from '../data/translations';
import { WHATSAPP_PHONE_RAW, WHATSAPP_PHONE_DISPLAY, createWhatsAppUrl } from '../utils/whatsapp';
import { SorenaLogo } from './SorenaLogo';
import { NavBar, NavItem } from './ui/tubelight-navbar';

interface MobileFloatingNavProps {
  lang: Language;
  onLanguageChange: (newLang: Language) => void;
  currentPage: AppPage;
  onNavigate: (page: AppPage) => void;
  onOpenQuote: () => void;
}

// Flags for language selection
const FlagIran: React.FC<{ className?: string }> = ({ className = "w-4 h-3" }) => (
  <svg className={`${className} rounded-[2px] shadow-sm shrink-0`} viewBox="0 0 640 400" aria-label="Iran">
    <rect width="640" height="133.3" fill="#239f40"/>
    <rect y="133.3" width="640" height="133.3" fill="#ffffff"/>
    <rect y="266.6" width="640" height="133.4" fill="#da0000"/>
    <circle cx="320" cy="200" r="22" fill="#da0000" opacity="0.85"/>
  </svg>
);

const FlagUK: React.FC<{ className?: string }> = ({ className = "w-4 h-3" }) => (
  <svg className={`${className} rounded-[2px] shadow-sm shrink-0`} viewBox="0 0 640 400" aria-label="United Kingdom">
    <rect width="640" height="400" fill="#012169"/>
    <path d="M0 0 L640 400 M640 0 L0 400" stroke="#ffffff" strokeWidth="60"/>
    <path d="M0 0 L640 400 M640 0 L0 400" stroke="#c8102e" strokeWidth="35"/>
    <path d="M320 0 V400 M0 200 H640" stroke="#ffffff" strokeWidth="95"/>
    <path d="M320 0 V400 M0 200 H640" stroke="#c8102e" strokeWidth="55"/>
  </svg>
);

export const MobileFloatingNav: React.FC<MobileFloatingNavProps> = ({
  lang,
  onLanguageChange,
  currentPage,
  onNavigate,
  onOpenQuote
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isRtl = lang === 'fa';
  const isFa = lang === 'fa';
  const Chevron = isRtl ? ChevronLeft : ChevronRight;
  const t = translations[lang];

  // Prevent background body scrolling when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  const whatsappDirectUrl = createWhatsAppUrl(
    isFa
      ? 'سلام مهندس کاویانی‌پور، برای مشاوره و ثبت سفارش طراحی وبسایت / اکسپرت از سایت پیام می‌دهم.'
      : 'Hello Mr. Kavianipoor, I am contacting you from the Sorena IT website for web/algo trading consultation.'
  );

  // Map AppPage to active tab name
  const pageToNameMap: Record<AppPage, string> = {
    'home': isFa ? 'خانه' : 'Home',
    'services': isFa ? 'خدمات' : 'Services',
    'algo-trading': isFa ? 'الگوترید' : 'Algo',
    'portfolio': isFa ? 'نمونه‌کار' : 'Work',
    'campaign': isFa ? 'منو' : 'Menu',
    'tech-comparison': isFa ? 'منو' : 'Menu',
    'calculator': isFa ? 'منو' : 'Menu',
    'why-us': isFa ? 'منو' : 'Menu',
    'faq': isFa ? 'منو' : 'Menu',
    'contact': isFa ? 'منو' : 'Menu',
    'blog': isFa ? 'منو' : 'Menu',
  };

  const activeTabName = drawerOpen 
    ? (isFa ? 'منو' : 'Menu') 
    : (pageToNameMap[currentPage] || (isFa ? 'خانه' : 'Home'));

  // NavItems for Tubelight Navbar
  const navItems: NavItem[] = [
    {
      name: isFa ? 'خانه' : 'Home',
      url: '#home',
      icon: Home,
      onClick: () => {
        setDrawerOpen(false);
        onNavigate('home');
      }
    },
    {
      name: isFa ? 'خدمات' : 'Services',
      url: '#services',
      icon: Code2,
      onClick: () => {
        setDrawerOpen(false);
        onNavigate('services');
      }
    },
    {
      name: isFa ? 'الگوترید' : 'Algo',
      url: '#algo-trading',
      icon: TrendingUp,
      onClick: () => {
        setDrawerOpen(false);
        onNavigate('algo-trading');
      }
    },
    {
      name: isFa ? 'نمونه‌کار' : 'Work',
      url: '#portfolio',
      icon: Briefcase,
      onClick: () => {
        setDrawerOpen(false);
        onNavigate('portfolio');
      }
    },
    {
      name: isFa ? 'منو' : 'Menu',
      url: '#menu',
      icon: drawerOpen ? X : Menu,
      onClick: () => {
        setDrawerOpen(!drawerOpen);
      }
    }
  ];

  // Additional drawer menu links for full exploration
  const drawerLinks = [
    {
      id: 'campaign' as AppPage,
      label: isFa ? 'کمپین تخفیف هرمی (۱۰۰ به ۳۰ م)' : 'Pyramid Campaign (70% OFF)',
      badge: isFa ? '۷۰٪ تخفیف' : '70% OFF',
      icon: Sparkles,
      color: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    },
    {
      id: 'calculator' as AppPage,
      label: isFa ? 'محاسبه‌گر هوشمند هزینه پروژه' : 'Project Cost Calculator',
      badge: isFa ? 'آنلاین' : 'Instant',
      icon: Calculator,
      color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
    },
    {
      id: 'tech-comparison' as AppPage,
      label: isFa ? 'مقایسه تکنولوژی‌ها (وردپرس vs کد)' : 'Tech Matrix (React vs CMS)',
      icon: Cpu,
      color: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    },
    {
      id: 'why-us' as AppPage,
      label: isFa ? 'چرا سورنا آی‌تی و رزومه مدیر' : 'Why Sorena IT & Management Profile',
      icon: ShieldCheck,
      color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    },
    {
      id: 'faq' as AppPage,
      label: isFa ? 'سوالات متداول کارفرمایان' : 'Frequently Asked Questions',
      icon: HelpCircle,
      color: 'text-sky-400 bg-sky-500/10 border-sky-500/30',
    },
    {
      id: 'blog' as AppPage,
      label: isFa ? 'مقالات و آموزش‌های تخصصی' : 'Technical Articles & Guides',
      icon: BookOpen,
      color: 'text-rose-400 bg-rose-500/10 border-rose-500/30',
    },
    {
      id: 'contact' as AppPage,
      label: isFa ? 'تماس مستقیم و ثبت سفارش' : 'Contact & Submit Order',
      icon: Mail,
      color: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
    }
  ];

  return (
    <div className="lg:hidden">
      {/* 1. Tubelight Floating Navbar (Mobile & Tablet) */}
      <NavBar 
        items={navItems} 
        activeTab={activeTabName}
        className="fixed bottom-3 sm:bottom-5 left-1/2 -translate-x-1/2 z-50 mb-0 pointer-events-auto"
      />

      {/* 2. Full-Screen Elegant Drawer when "Menu" is active */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 transition-all duration-300">
          {/* Backdrop */}
          <div
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 bg-black/80 backdrop-blur-md transition-opacity duration-300"
          />

          {/* Slide-up Sheet */}
          <div
            className="fixed bottom-0 left-0 right-0 z-50 max-h-[85vh] overflow-y-auto bg-gradient-to-b from-[#140b06] via-[#0f0703] to-[#070301] border-t border-amber-500/30 rounded-t-3xl shadow-[0_-12px_45px_rgba(0,0,0,0.85)] p-5 pb-24 text-stone-200 transition-all duration-300 ease-out"
          >
            {/* Drawer Top Handle */}
            <div className="w-12 h-1.5 bg-stone-700/70 rounded-full mx-auto mb-4" />

              {/* Drawer Header with Logo and Language Toggle */}
              <div className="flex items-center justify-between border-b border-stone-800/80 pb-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 shrink-0 rounded-xl bg-gradient-to-tr from-amber-400 via-yellow-400 to-amber-600 p-[1px] shadow-md shadow-amber-500/20">
                    <div className="w-full h-full bg-[#100804] rounded-[11px] p-1 flex items-center justify-center">
                      <SorenaLogo className="w-full h-full" glow />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base font-black text-white">
                      {lang === 'fa' ? 'سورنا آی‌تی' : 'Sorena IT'}
                    </h3>
                    <p className="text-[11px] text-stone-400">
                      {lang === 'fa' ? 'مهندسی وب و الگوتریدینگ پیشرفته' : 'Web & Algo Trading Engineering'}
                    </p>
                  </div>
                </div>

                {/* Language Switcher */}
                <div className="flex items-center bg-stone-900/90 border border-stone-800 p-1 rounded-full gap-1">
                  <button
                    onClick={() => onLanguageChange('fa')}
                    aria-label="فارسی"
                    className={`flex items-center justify-center p-1.5 rounded-full transition-all ${
                      lang === 'fa'
                        ? 'bg-amber-400 text-stone-950 shadow-sm'
                        : 'text-stone-400 hover:text-white'
                    }`}
                  >
                    <FlagIran className="w-4 h-3" />
                  </button>
                  <button
                    onClick={() => onLanguageChange('en')}
                    aria-label="English"
                    className={`flex items-center justify-center p-1.5 rounded-full transition-all ${
                      lang === 'en'
                        ? 'bg-amber-400 text-stone-950 shadow-sm'
                        : 'text-stone-400 hover:text-white'
                    }`}
                  >
                    <FlagUK className="w-4 h-3" />
                  </button>
                </div>
              </div>

              {/* Quick Actions Grid */}
              <div className="grid grid-cols-2 gap-2.5 mb-4">
                <button
                  onClick={() => {
                    setDrawerOpen(false);
                    onOpenQuote();
                  }}
                  className="flex items-center gap-2 p-3 rounded-2xl bg-gradient-to-br from-amber-500/15 to-yellow-500/5 border border-amber-500/30 text-start group"
                >
                  <div className="w-8 h-8 rounded-xl bg-amber-400/20 text-amber-400 flex items-center justify-center shrink-0">
                    <Calculator className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-white group-hover:text-amber-300">
                      {isFa ? 'استعلام قیمت' : 'Cost Calculator'}
                    </div>
                    <div className="text-[10px] text-amber-400/80">
                      {isFa ? 'محاسبه آنلاین' : 'Instant Estimate'}
                    </div>
                  </div>
                </button>

                <a
                  href={whatsappDirectUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 rounded-2xl bg-gradient-to-br from-emerald-500/15 to-teal-500/5 border border-emerald-500/30 text-start group"
                >
                  <div className="w-8 h-8 rounded-xl bg-emerald-400/20 text-emerald-400 flex items-center justify-center shrink-0">
                    <MessageCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-white group-hover:text-emerald-300">
                      {isFa ? 'واتس‌اپ مدیر' : 'WhatsApp Chat'}
                    </div>
                    <div className="text-[10px] text-emerald-400/80">
                      {isFa ? 'پاسخگویی سریع' : 'Fast Response'}
                    </div>
                  </div>
                </a>
              </div>

              {/* Comprehensive Navigation List */}
              <div className="space-y-1.5 mb-5">
                <div className="text-[11px] font-bold text-stone-500 uppercase tracking-wider px-2 mb-1">
                  {isFa ? 'صفحات و بخش‌های سایت' : 'Directory & Pages'}
                </div>

                {drawerLinks.map((link) => {
                  const Icon = link.icon;
                  const isCurrent = currentPage === link.id;

                  return (
                    <button
                      key={link.id}
                      onClick={() => {
                        setDrawerOpen(false);
                        onNavigate(link.id);
                      }}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all text-start cursor-pointer ${
                        isCurrent
                          ? 'bg-amber-500/15 border-amber-500/40 text-amber-300 font-bold'
                          : 'bg-stone-900/50 border-stone-800/80 hover:bg-stone-800/70 text-stone-300'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center border ${link.color}`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-xs font-semibold">{link.label}</span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        {link.badge && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/30">
                            {link.badge}
                          </span>
                        )}
                        <Chevron className="w-4 h-4 text-stone-500" />
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Contact Footer Info */}
              <div className="p-3.5 rounded-2xl bg-stone-900/60 border border-stone-800 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-stone-300">
                  <PhoneCall className="w-4 h-4 text-amber-400 shrink-0" />
                  <span dir="ltr" className="font-mono text-xs font-semibold text-left inline-block">{WHATSAPP_PHONE_DISPLAY}</span>
                </div>
                <span className="text-[11px] text-stone-400">
                  {lang === 'fa' ? 'کرج / تهران' : 'Karaj / Tehran'}
                </span>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};
